// utilities/uraMapping.js
// ---------------------------------------------------------------------------
// ONE place for how the dashboard reads and writes URA reference codes on the
// catalogue: the preset envelope the server returns, the fields a category or
// unit write carries, and the resolution "which URA code does this product
// file under" that every listing form shows beside its category and unit.
//
// A mapped code is a reference lookup, not a filed document. Nothing in this
// module (or the screens that use it) is a statement of compliance.
//
// Mirrors, on the server side:
//   controllers/categoryController.js   GET /category/ura-presets
//   controllers/siUnitController.js     GET /unit/ura-presets
//   services/fiscal/uraCatalogFields.js the exact fields a write accepts
//   models/shared/productTaxFields.js   goodsCategoryId / unitOfMeasure on products

export const URA_NOT_MAPPED_TEXT = "Not mapped to URA yet";
export const URA_NOT_LOADED_TEXT =
  "URA reference data is not loaded on this server yet";

/** Shortest query the server searches; anything shorter must be an exact code. */
export const URA_MIN_QUERY_LENGTH = 2;

const str = (value) =>
  value === null || value === undefined ? null : String(value).trim() || null;

/**
 * The preset envelope, tolerant of both shapes the handler has had:
 *   { items, present, total }   the hardened contract
 *   { items, present, count }   the earlier one (count is the sliced length)
 *   [ ... ]                     a bare array
 */
export const normaliseUraPresetResponse = (data) => {
  const items = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
    ? data.items
    : [];
  const total =
    typeof data?.total === "number"
      ? data.total
      : typeof data?.count === "number"
      ? data.count
      : items.length;
  return {
    items,
    total,
    present: !(data && data.present === false),
  };
};

/** One commodity leaf as the picker shows it: { code, name, rate }. */
export const normaliseUraLeaf = (item) => {
  if (!item) return null;
  const code = str(
    item.code ?? item.commodityCategoryCode ?? item.value ?? item.uraCommodityCode
  );
  if (!code) return null;
  return {
    code,
    name: str(
      item.name ?? item.commodityCategoryName ?? item.label ?? item.uraCommodityName
    ),
    rate: str(item.rate ?? item.taxRate ?? item.uraTaxRate),
    parentCode: str(item.parentCode),
    isService: item.isService === true,
    excisable: item.excisable === true,
  };
};

/** One measure unit as the picker shows it: { code, name }. */
export const normaliseUraUnit = (item) => {
  if (!item) return null;
  const code = str(item.code ?? item.value ?? item.uraMeasureUnit);
  if (!code) return null;
  return {
    code,
    name: str(item.name ?? item.label ?? item.uraMeasureUnitName),
  };
};

/** Category document fields -> picker value, or null when unmapped. */
export const leafFromCategory = (category) =>
  category?.uraCommodityCode
    ? normaliseUraLeaf({
        code: category.uraCommodityCode,
        name: category.uraCommodityName,
        rate: category.uraTaxRate,
      })
    : null;

/** Unit document fields -> picker value, or null when unmapped. */
export const unitFromSiUnit = (unit) =>
  unit?.uraMeasureUnit
    ? normaliseUraUnit({ code: unit.uraMeasureUnit, name: unit.uraMeasureUnitName })
    : null;

/**
 * The body fields a category write carries for its mapping. Passing null
 * clears the mapping: the server writes any field PRESENT in the body, and
 * null is present, undefined is not.
 */
export const categoryUraMappingFromLeaf = (leaf) => ({
  uraCommodityCode: leaf?.code || null,
  uraCommodityName: leaf?.code ? leaf.name || null : null,
  uraTaxRate: leaf?.code ? leaf.rate || null : null,
});

/** The body fields a unit write carries for its mapping; null clears it. */
export const unitUraMappingFromPreset = (unit) => ({
  uraMeasureUnit: unit?.code || null,
  uraMeasureUnitName: unit?.code ? unit.name || null : null,
});

/** "0.18" -> "18%". Anything that is not a plain fraction is shown as given. */
export const formatUraRate = (rate) => {
  const text = str(rate);
  if (!text) return null;
  const n = Number(text);
  if (!Number.isFinite(n)) return text;
  if (n > 1) return `${n}%`;
  return `${Math.round(n * 10000) / 100}%`;
};

const idOf = (ref) => {
  if (!ref) return null;
  if (typeof ref === "string") return ref;
  return str(ref._id ?? ref.value ?? ref.id);
};

const asArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

/** Find the full document behind an id, an option {value}, or the doc itself. */
const resolveDoc = (ref, list, codeField) => {
  if (!ref) return null;
  if (typeof ref === "object" && ref[codeField] !== undefined) return ref;
  const id = idOf(ref);
  if (!id) return typeof ref === "object" ? ref : null;
  const found = asArray(list).find((doc) => idOf(doc) === id);
  return found || (typeof ref === "object" ? ref : null);
};

const labelOf = (doc, field) => str(doc?.[field] ?? doc?.label ?? doc?.name);

/**
 * Which URA codes a product files under, for display on a listing form.
 *
 *   product     anything shaped like a catalogue item: `goodsCategoryId`,
 *               `unitOfMeasure` (its own codes, set on the Fiscal
 *               Classification screen), `category` (ids, documents or select
 *               options, one or many) and `unit_id` (id, document or option).
 *   categories  category documents to resolve ids and bare options against.
 *   units       siUnit documents, likewise.
 *
 * Precedence, per code: the product's own value, else the mapped category or
 * unit, else nothing. Returns
 *   { commodity: { code, name, rate, source, sourceLabel, conflict },
 *     unit:      { code, name, source, sourceLabel } }
 * with `source` one of "product", "category", "unit" or null, and `conflict`
 * true when the selected categories carry different URA codes (the product
 * then needs its own code to be unambiguous).
 */
export const resolveUraCodesForDisplay = (product, categories = [], units = []) => {
  const p = product || {};

  const categoryDocs = asArray(p.category ?? p.categories)
    .map((ref) => resolveDoc(ref, categories, "uraCommodityCode"))
    .filter(Boolean);
  const mappedCategories = categoryDocs.filter((doc) => str(doc.uraCommodityCode));
  const distinctCodes = new Set(mappedCategories.map((doc) => str(doc.uraCommodityCode)));

  let commodity = { code: null, name: null, rate: null, source: null, sourceLabel: null, conflict: false };
  if (str(p.goodsCategoryId)) {
    commodity = {
      code: str(p.goodsCategoryId),
      name: null,
      rate: null,
      source: "product",
      sourceLabel: "set on this product",
      conflict: false,
    };
  } else if (mappedCategories.length) {
    const first = mappedCategories[0];
    commodity = {
      code: str(first.uraCommodityCode),
      name: str(first.uraCommodityName),
      rate: str(first.uraTaxRate),
      source: "category",
      sourceLabel: labelOf(first, "category"),
      conflict: distinctCodes.size > 1,
    };
  }

  const unitDoc = resolveDoc(p.unit_id ?? p.unit, units, "uraMeasureUnit");
  let unit = { code: null, name: null, source: null, sourceLabel: null };
  if (str(p.unitOfMeasure)) {
    unit = {
      code: str(p.unitOfMeasure),
      name: null,
      source: "product",
      sourceLabel: "set on this product",
    };
  } else if (str(unitDoc?.uraMeasureUnit)) {
    unit = {
      code: str(unitDoc.uraMeasureUnit),
      name: str(unitDoc.uraMeasureUnitName),
      source: "unit",
      sourceLabel: labelOf(unitDoc, "unit"),
    };
  }

  return { commodity, unit };
};
